<template>
  <div class="accordion-container">
    <div
      v-for="(item, index) in items"
      :key="index"
      :class="['accordion-item', { expanded: expandedItems.includes(index) }]"
    >
      <button class="accordion-header" @click="toggleItem(index)">
        <div class="accordion-header-content">
          <i v-if="item.icon" :class="item.icon"></i>
          <span class="accordion-title">{{ item.title }}</span>
        </div>
        <i :class="['fas', expandedItems.includes(index) ? 'fa-chevron-up' : 'fa-chevron-down', 'accordion-icon']"></i>
      </button>
      <transition name="accordion">
        <div v-if="expandedItems.includes(index)" class="accordion-content">
          <div v-html="item.content"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface AccordionItem {
  title: string
  icon?: string
  content: string
}

const props = defineProps<{
  items: AccordionItem[]
  defaultExpanded?: number[]
  multiExpand?: boolean
}>()

const expandedItems = ref<number[]>(props.defaultExpanded || [0])

function toggleItem(index: number) {
  if (props.multiExpand) {
    // Allow multiple items to be expanded
    const idx = expandedItems.value.indexOf(index)
    if (idx > -1) {
      expandedItems.value.splice(idx, 1)
    } else {
      expandedItems.value.push(index)
    }
  } else {
    // Only one item expanded at a time
    expandedItems.value = expandedItems.value.includes(index) ? [] : [index]
  }
}
</script>

<style scoped>
.accordion-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0;
}

.accordion-item {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid rgba(139, 92, 246, 0.1);
}

.accordion-item.expanded {
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.3);
}

.accordion-header {
  width: 100%;
  padding: 18px 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.accordion-header:hover {
  background: rgba(139, 92, 246, 0.05);
}

.accordion-item.expanded .accordion-header {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.05));
}

.accordion-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.accordion-header-content i {
  font-size: 18px;
  color: #3b82f6;
}

.accordion-title {
  font-size: 16px;
  font-weight: 700;
  color: #1E293B;
  text-align: left;
}

.accordion-icon {
  font-size: 14px;
  color: #64748B;
  transition: transform 0.3s ease;
}

.accordion-item.expanded .accordion-icon {
  transform: rotate(180deg);
}

.accordion-content {
  padding: 0 24px 24px;
  color: #475569;
  line-height: 1.7;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 1000px;
  opacity: 1;
}
</style>
